import { useQuery } from '@apollo/client';
import { deduplicateLastByKey } from '@sixfold/typed-primitives';
import { useParams } from 'react-router';

import {
  companyCarrierToursByShipperMetricsQuery,
  companyShipperToursByCarrierMetricsQuery,
} from '../../company/graphql';
import { InfiniteScroll } from '../../components/infinitescroll';
import {
  CompanyCarrierToursByShippersMetricsQuery,
  CompanyCarrierToursByShippersMetricsQueryVariables,
  CompanyShipperToursByCarriersMetricsQuery,
  CompanyShipperToursByCarriersMetricsQueryVariables,
  getNodes,
  loadMoreFromConnection,
  PAGINATION_PAGE_SIZE,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { indexValueToPercentage } from '../../lib/util/string';
import { MetricsView, MetricsViewDataProps } from '../components/metrics';

interface Company {
  company_id: string;
  company_name: string | null;
}

interface MetricsContainerProps {
  show: 'carriers' | 'customers';
}

class LoadingMetricsByCarriersContainer extends Loader<
  CompanyShipperToursByCarriersMetricsQuery,
  MetricsViewDataProps,
  CompanyShipperToursByCarriersMetricsQueryVariables
> {}

class LoadingMetricsByShippersContainer extends Loader<
  CompanyCarrierToursByShippersMetricsQuery,
  MetricsViewDataProps,
  CompanyCarrierToursByShippersMetricsQueryVariables
> {}

const mapRowData = (
  company: Company | null,
  data: { visibilityIndex: number | null; toursCount: number; allocatedToursCount: number },
) => ({
  company,
  transport_count: data.toursCount,
  allocated_count: data.allocatedToursCount,
  visibility_index: indexValueToPercentage(data.visibilityIndex),
  percent_allocated: (data.toursCount !== 0 ? (data.allocatedToursCount / data.toursCount) * 100 : 0).toFixed(2),
});

const mapMetricsData = ({
  data,
}: {
  data: Partial<CompanyCarrierToursByShippersMetricsQuery | CompanyShipperToursByCarriersMetricsQuery>;
}) => {
  const company = data.company;

  if (company === undefined || company === null) {
    return { rows: [], company: null };
  }

  if ('tourReportsByShipper' in company) {
    return {
      rows: getNodes(company.tourReportsByShipper).map((row) => mapRowData(row.shipper, row)),
      company,
    };
  }

  return { rows: getNodes(company.tourReportsByCarrier).map((row) => mapRowData(row.carrier, row)), company };
};

export const MetricsContainer = ({ show }: MetricsContainerProps) => {
  const { company_id } = useParams() as { company_id: string };

  const metricsVariables = {
    company_id,
    first: PAGINATION_PAGE_SIZE,
  };

  const carrierResult = useQuery<
    CompanyShipperToursByCarriersMetricsQuery,
    CompanyShipperToursByCarriersMetricsQueryVariables
  >(companyShipperToursByCarrierMetricsQuery, {
    variables: metricsVariables,
    skip: show !== 'carriers',
  });

  const shipperResult = useQuery<
    CompanyCarrierToursByShippersMetricsQuery,
    CompanyCarrierToursByShippersMetricsQueryVariables
  >(companyCarrierToursByShipperMetricsQuery, {
    variables: metricsVariables,
    skip: show !== 'customers',
  });

  if (show === 'carriers') {
    return (
      <LoadingMetricsByCarriersContainer result={carrierResult} mapData={mapMetricsData}>
        {({ rows, company }) => {
          const deduplicatedRows = deduplicateLastByKey(rows, ({ company }) => company?.company_id ?? '');
          return (
            <InfiniteScroll
              loadMoreEntries={() =>
                loadMoreFromConnection(companyShipperToursByCarrierMetricsQuery, carrierResult, [
                  'company',
                  'tourReportsByCarrier',
                ])
              }>
              <MetricsView rows={deduplicatedRows} company={company} show={show} />
            </InfiniteScroll>
          );
        }}
      </LoadingMetricsByCarriersContainer>
    );
  }

  return (
    <LoadingMetricsByShippersContainer result={shipperResult} mapData={mapMetricsData}>
      {({ rows, company }) => {
        const deduplicatedRows = deduplicateLastByKey(rows, ({ company }) => company?.company_id ?? '');
        return (
          <InfiniteScroll
            loadMoreEntries={() =>
              loadMoreFromConnection(companyCarrierToursByShipperMetricsQuery, shipperResult, [
                'company',
                'tourReportsByShipper',
              ])
            }>
            <MetricsView rows={deduplicatedRows} company={company} show={show} />
          </InfiniteScroll>
        );
      }}
    </LoadingMetricsByShippersContainer>
  );
};
